import { DataSource } from '@client/core/data/concerns/data-source';
import { fromJson } from '@client/core/data/concerns/parses-from-json';
import { JsonResponse } from '@client/core/http/types';
import { Attachment } from '@client/features/project-documents/models/attachment';
import { CompanyProfile } from '@client/features/company-profile/models/company-profile';
import { AboutCompanyFieldName } from '@client/features/company-profile/types';

export interface UpdateCompanyProfile {
  name: CompanyProfile['name'];
  email: CompanyProfile['email'];
  phone: CompanyProfile['phone'];
  website: CompanyProfile['website'];
}

export class CompanyProfileDataSource extends DataSource {
  /**
   * Find the Company model that represents the Workspace Owner's Company.
   *
   * @param ownerId {Number} User.id of Workspace Owner
   */
  async findCompanyProfile(ownerId: number): Promise<null | CompanyProfile> {
    const uri = `/workspace/${ownerId}/company-profile`;
    const response = await this.httpClient.get<JsonResponse>(uri);

    return fromJson(CompanyProfile, response.data.data);
  }

  /**
   * @deprecated Use updateAboutCompanyField()
   */
  async updateCompanyAsWorkspaceCompany(ownerId: number, attributes: UpdateCompanyProfile): Promise<CompanyProfile> {
    const uri = `/workspace/${ownerId}/company-profile`;
    const response = await this.httpClient.patch<JsonResponse>(uri, attributes);

    return fromJson(CompanyProfile, response.data.data);
  }

  /**
   * TODO: Add support for Name, Website, Phone, Email instead of updateCompanyAsWorkspaceCompany(). Add Packet Data fields to CompanyProfile model
   */
  async updateAboutCompanyField(ownerId: number, field: AboutCompanyFieldName, newValue: string): Promise<void> {
    const payload = {};
    payload[field] = newValue;

    const uri = `/workspace/${ownerId}/company-profile`;

    await this.httpClient.patch<JsonResponse>(uri, payload);
  }

  /**
   * Submit the given File as the Workspace Company's new Report Logo.
   */
  async uploadNewReportLogo(ownerId: number, file: File): Promise<Attachment> {
    const formData = new FormData();
    formData.append('report_logo', file);

    const uri = `/workspace/${ownerId}/company-profile/report-logo`;
    const fileUploadConfig = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };
    const response = await this.httpClient.post<JsonResponse>(uri, formData, fileUploadConfig);

    return fromJson(Attachment, response.data.data);
  }

  /**
   * Submit the given File as the Workspace Company's new Navbar Logo.
   */
  async uploadNewNavbarLogo(ownerId: number, file: File): Promise<Attachment> {
    const formData = new FormData();
    formData.append('navbar_logo', file);

    const uri = `/workspace/${ownerId}/company-profile/navbar-logo`;
    const fileUploadConfig = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };
    const response = await this.httpClient.post<JsonResponse>(uri, formData, fileUploadConfig);

    return fromJson(Attachment, response.data.data);
  }
}
